import {
  touchSession,
  trackContactChannel,
  trackEngagement,
  trackInteraction,
  trackOutboundClick,
  trackScrollDepth,
  trackSectionView
} from "./analytics";

/*
 * Automatic instrumentation.
 *
 * Everything here works by DELEGATION — one listener on the document, one
 * IntersectionObserver, one timer — rather than by adding an onClick to every
 * component. That matters for two reasons: contact links are rendered from at
 * least six different components (header, contact dock, footer, lead form,
 * contact page, Zeta CTA), and any future component gets tracked for free
 * instead of being silently missed.
 *
 * What it answers:
 *   intent      who tried to reach a human, from where  (contact_channel_click)
 *   interest    which sections were actually seen, how deep people scrolled,
 *               which accordions/tabs/slides they opened
 *   attention   engaged seconds — foreground and non-idle only, so a tab left
 *               open in the background does not read as engagement
 *   referral    clicks out to live client projects        (outbound_click)
 */

/* Attention is only counted while the tab is visible AND the person has done
   something in the last IDLE_MS. Without the idle check, "time on page" is
   mostly abandoned tabs. */
const IDLE_MS = 30 * 1000;
const TICK_MS = 1000;
const ENGAGEMENT_MILESTONES = [15, 30, 60, 120, 300, 600];
const SCROLL_MILESTONES = [25, 50, 75, 90, 100];

let started = false;
let cleanupFns = [];

/* per-page-view state, reset on route change */
let firedScroll = new Set();
let seenSections = new Set();
let engagedSeconds = 0;
let firedEngagement = new Set();
let lastActivityAt = Date.now();
let currentPath = typeof window !== "undefined" ? window.location.pathname : "/";

/* ------------------------------------------------------------------ utils -- */

/** Where on the page did this happen? Prefers an explicit opt-in marker. */
function resolveSource(el) {
  const explicit = el.closest("[data-analytics-source]");
  if (explicit) {
    return explicit.getAttribute("data-analytics-source");
  }

  const section = el.closest("section[id]");
  if (section) {
    return section.id;
  }

  if (el.closest("header")) return "header";
  if (el.closest("footer")) return "footer";
  if (el.closest("form")) return "form";
  return "page";
}

/** Readable label for a link, without dumping a whole paragraph into GA. */
function resolveLabel(el) {
  const aria = el.getAttribute("aria-label");
  if (aria) return aria.trim().slice(0, 80);
  const text = (el.textContent || "").replace(/\s+/g, " ").trim();
  if (text) return text.slice(0, 80);
  const title = el.getAttribute("title");
  return title ? title.trim().slice(0, 80) : "(no label)";
}

function isSameHost(url) {
  try {
    return new URL(url, window.location.origin).hostname === window.location.hostname;
  } catch (error) {
    return true;
  }
}

/* ------------------------------------------------------------------ clicks -- */

function onDocumentClick(event) {
  markActivity();

  const target = event.target;
  if (!target || typeof target.closest !== "function") {
    return;
  }

  /* --- explicit interest markers -------------------------------------
     Any element can opt in with data-analytics-interaction="type:label",
     which is how accordions/tabs/slides report without importing anything. */
  const marked = target.closest("[data-analytics-interaction]");
  if (marked) {
    const raw = marked.getAttribute("data-analytics-interaction") || "";
    const [type, ...rest] = raw.split(":");
    trackInteraction(
      (type || "interaction").trim(),
      (rest.join(":") || resolveLabel(marked)).trim(),
      { source: resolveSource(marked) }
    );
  }

  const link = target.closest("a[href]");
  if (!link) {
    return;
  }

  const href = link.getAttribute("href") || "";
  const source = resolveSource(link);
  const label = resolveLabel(link);

  if (href.startsWith("tel:")) {
    trackContactChannel("call", source, { contact_label: label });
    return;
  }

  if (href.startsWith("mailto:")) {
    trackContactChannel("email", source, { contact_label: label });
    return;
  }

  /* wa.me and api.whatsapp.com both mean the same intent. */
  if (/(?:wa\.me|api\.whatsapp\.com|web\.whatsapp\.com)/i.test(href)) {
    trackContactChannel("whatsapp", source, { contact_label: label });
    return;
  }

  if (/^https?:\/\//i.test(href) && !isSameHost(href)) {
    trackOutboundClick(href, label, source);
  }
}

/* ------------------------------------------------------------------ scroll -- */

function onScroll() {
  markActivity();

  const doc = document.documentElement;
  const scrollable = doc.scrollHeight - window.innerHeight;
  if (scrollable <= 0) {
    return;
  }

  const percent = Math.min(
    100,
    Math.round(((window.scrollY || window.pageYOffset || 0) / scrollable) * 100)
  );

  SCROLL_MILESTONES.forEach((milestone) => {
    if (percent >= milestone && !firedScroll.has(milestone)) {
      firedScroll.add(milestone);
      trackScrollDepth(milestone);
    }
  });
}

/* -------------------------------------------------------------- engagement -- */

function markActivity() {
  lastActivityAt = Date.now();
  touchSession();
}

function onTick() {
  if (document.visibilityState !== "visible") {
    return;
  }
  if (Date.now() - lastActivityAt > IDLE_MS) {
    return;
  }

  engagedSeconds += 1;

  ENGAGEMENT_MILESTONES.forEach((milestone) => {
    if (engagedSeconds === milestone && !firedEngagement.has(milestone)) {
      firedEngagement.add(milestone);
      trackEngagement(milestone, "milestone", {
        sections_seen: seenSections.size,
        max_scroll_percent: firedScroll.size ? Math.max(...firedScroll) : 0
      });
    }
  });
}

/* One final summary per page view — the row that says how much of the page a
   person actually consumed before leaving. */
function flushEngagement(reason) {
  if (engagedSeconds <= 0) {
    return;
  }
  trackEngagement(engagedSeconds, reason, {
    sections_seen: seenSections.size,
    max_scroll_percent: firedScroll.size ? Math.max(...firedScroll) : 0,
    exit_path: currentPath
  });
}

function onVisibilityChange() {
  if (document.visibilityState === "hidden") {
    flushEngagement("page_hidden");
  } else {
    markActivity();
  }
}

/* ---------------------------------------------------------- section views -- */

let sectionObserver = null;

function observeSections() {
  if (typeof IntersectionObserver === "undefined") {
    return;
  }

  if (sectionObserver) {
    sectionObserver.disconnect();
  }

  sectionObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const id = entry.target.id;
        if (!id || seenSections.has(id)) return;
        seenSections.add(id);
        trackSectionView(id, seenSections.size);
      });
    },
    /* half the section on screen — enough to count as "seen", not just skimmed */
    { threshold: 0.5 }
  );

  document.querySelectorAll("main section[id]").forEach((el) => {
    sectionObserver.observe(el);
  });
}

/* ------------------------------------------------------------------- api --- */

/** Start the delegated listeners. Safe to call once; returns a cleanup fn. */
export function startAutoTracking() {
  if (typeof window === "undefined" || started) {
    return () => {};
  }
  started = true;

  const activityEvents = ["pointerdown", "keydown", "touchstart"];
  const tickId = window.setInterval(onTick, TICK_MS);

  document.addEventListener("click", onDocumentClick, true);
  window.addEventListener("scroll", onScroll, { passive: true });
  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("pagehide", () => flushEngagement("page_hide"));
  activityEvents.forEach((name) =>
    document.addEventListener(name, markActivity, { passive: true })
  );

  observeSections();

  cleanupFns = [
    () => window.clearInterval(tickId),
    () => document.removeEventListener("click", onDocumentClick, true),
    () => window.removeEventListener("scroll", onScroll),
    () => document.removeEventListener("visibilitychange", onVisibilityChange),
    ...activityEvents.map(
      (name) => () => document.removeEventListener(name, markActivity)
    ),
    () => sectionObserver && sectionObserver.disconnect()
  ];

  return stopAutoTracking;
}

export function stopAutoTracking() {
  cleanupFns.forEach((fn) => {
    try {
      fn();
    } catch (error) {
      /* nothing useful to do on teardown */
    }
  });
  cleanupFns = [];
  sectionObserver = null;
  started = false;
}

/**
 * Reset the per-page counters on a route change, after flushing what the
 * previous page earned — otherwise a single-page app reports one enormous
 * session-long "page" and every depth metric is meaningless.
 */
export function resetPageTracking(pathname) {
  if (typeof window === "undefined") {
    return;
  }

  flushEngagement("route_change");

  currentPath = pathname;
  firedScroll = new Set();
  seenSections = new Set();
  firedEngagement = new Set();
  engagedSeconds = 0;
  lastActivityAt = Date.now();

  /* the new route mounts its own sections — re-observe after paint */
  window.requestAnimationFrame(() => observeSections());
}
